// src/components/business/NewBusinessModal.tsx
//
// Modal "Novo negócio" (correção UX v2, C2 — decisão Breno): substitui o
// BusinessWizard inline da BusinessesPage. Anatomia do mockup: caixa de modal
// do wireframe (BizModal/WfModal) + form .nc-field/.nc-2col da aba
// Configurações da seção 04 (sem Status — negócio novo nasce draft).
//
// Criação: POST /v1/businesses (nome, tipo, subtipo) e, se houver campos de
// DNA preenchidos, PATCH /v1/businesses/:id/dna (mercado, público, modelo).

import { useState } from 'react';
import { api, type Business, type BusinessType } from '@/lib/api';
import { BizModal } from './BizModal';
import { WfButton } from '@/components/wf';
import { Briefcase } from 'lucide-react';
import { REVENUE_GOAL_OPTIONS, TYPE_OPTIONS } from './business-constants';

interface NewBusinessModalProps {
  open: boolean;
  onClose: () => void;
  /** Chamado com o negócio criado (DNA já salvo, quando informado). */
  onCreated: (biz: Business) => void;
}

export function NewBusinessModal({ open, onClose, onCreated }: NewBusinessModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<BusinessType>('product');
  const [subtype, setSubtype] = useState('');
  const [revenueModel, setRevenueModel] = useState('');
  const [market, setMarket] = useState('');
  const [audience, setAudience] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setName('');
    setType('product');
    setSubtype('');
    setRevenueModel('');
    setMarket('');
    setAudience('');
    setError('');
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Informe o nome do negócio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const biz = await api.createBusiness({
        name: name.trim(),
        type,
        subtype: subtype.trim() || undefined,
      });
      if (market.trim() || audience.trim() || revenueModel) {
        // DNA é best-effort: o negócio já existe; falha aqui não bloqueia.
        await api
          .updateBusinessDNA(biz.id, {
            market: market.trim() || null,
            audience: audience.trim() || null,
            revenue_model: revenueModel || null,
          })
          .catch(() => undefined);
      }
      reset();
      onCreated(biz);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar negócio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BizModal
      open={open}
      onClose={handleClose}
      title="Novo negócio"
      icon={<Briefcase className="ic" aria-hidden="true" />}
      footer={
        <>
          <WfButton variant="ghost" onClick={handleClose} disabled={saving}>
            Cancelar
          </WfButton>
          <WfButton variant="primary" onClick={handleCreate} disabled={saving || !name.trim()}>
            {saving ? 'Criando…' : 'Criar negócio'}
          </WfButton>
        </>
      }
    >
      <div className="wf-nc-2col">
        <div className="wf-field">
          <label className="wf-label" htmlFor="nb-name">Nome do negócio</label>
          <input
            id="nb-name"
            className="wf-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex: Transcreve ADV"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="nb-type">Tipo</label>
          <select
            id="nb-type"
            className="wf-select"
            value={type}
            onChange={(e) => setType(e.target.value as BusinessType)}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wf-nc-2col">
        <div className="wf-field">
          <label className="wf-label" htmlFor="nb-subtype">Subtipo</label>
          <input
            id="nb-subtype"
            className="wf-input"
            value={subtype}
            onChange={(e) => setSubtype(e.target.value)}
            placeholder="ex: saas, marketplace"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="nb-revenue">Modelo de receita</label>
          <select
            id="nb-revenue"
            className="wf-select"
            value={revenueModel}
            onChange={(e) => setRevenueModel(e.target.value)}
          >
            <option value="">—</option>
            {REVENUE_GOAL_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wf-field">
        <label className="wf-label" htmlFor="nb-market">Mercado (DNA)</label>
        <input
          id="nb-market"
          className="wf-input"
          value={market}
          onChange={(e) => setMarket(e.target.value)}
          placeholder="mercado-alvo"
        />
      </div>

      <div className="wf-field">
        <label className="wf-label" htmlFor="nb-audience">Público (DNA)</label>
        <textarea
          id="nb-audience"
          className="wf-textarea"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          placeholder="audiência / persona"
        />
      </div>

      {error && <p className="text-xs" style={{ color: 'var(--wf-danger-text)' }}>{error}</p>}
    </BizModal>
  );
}
